import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getWorkerRequestContests from './getWorkerRequestContests.graphql';
import resolveWorkerRequestContestM from './resolveWorkerRequestContest.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';

// Admin queue for client contests of worker mid-job requests (time / materials / workers).
// Approve applies the request exactly as the client accepting would (delegated server-side), with
// an optional fair-price adjustment; Reject declines it (worker gets nothing extra).
class JobContests extends Component {
  constructor(props) {
    super(props);
    this.state = { tab: 'open', busy: false, message: '', currentPage: 1 };
  }

  refetch = () => {
    const d = this.props.contestsData;
    if (d && d.refetch) d.refetch({ currentPage: this.state.currentPage, searchList: this.state.tab });
  };

  switchTab = (tab) => {
    this.setState({ tab, message: '', currentPage: 1 }, this.refetch);
  };

  go = (delta) => this.setState((s) => ({ currentPage: Math.max(1, s.currentPage + delta) }), this.refetch);

  // Ask for the per-type adjustment, then resolve.
  resolve = async (c, decision) => {
    const variables = { contestId: c.id, decision };
    if (decision === 'approve') {
      if (c.requestType === 'time') {
        const cur = c.requestMinutes != null ? String(c.requestMinutes) : '';
        const v = window.prompt(`Approve EXTRA TIME for booking #${c.bookingId}.\nGranted minutes (leave as-is to accept the worker's ask):`, cur);
        if (v === null) return;
        if (v !== '' && v !== cur) variables.adjustedMinutes = parseInt(v, 10);
      } else if (c.requestType === 'materials') {
        const cur = c.requestAmount != null ? String(c.requestAmount) : '';
        const v = window.prompt(`Approve MATERIALS / fee for booking #${c.bookingId}.\nFair amount in ${c.currency || ''} (leave as-is to accept the worker's ask):`, cur);
        if (v === null) return;
        if (v !== '' && v !== cur) variables.adjustedAmount = parseFloat(v);
      } else if (c.requestType === 'workers') {
        const cur = c.requestCount != null ? String(c.requestCount) : '';
        const v = window.prompt(`Approve EXTRA WORKERS for booking #${c.bookingId}.\nHelper count (leave as-is to accept the worker's ask):`, cur);
        if (v === null) return;
        if (v !== '' && v !== cur) variables.adjustedCount = parseInt(v, 10);
      }
    } else {
      if (!window.confirm(`Reject this ${c.requestType} request on booking #${c.bookingId}? The worker gets nothing extra.`)) return;
    }
    const note = window.prompt('Admin note (optional, kept on the case):', '');
    if (note === null) return;
    if (note) variables.note = note;

    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.resolveMut({ variables });
      const r = res && res.data && res.data.resolveWorkerRequestContest;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: `Contest #${c.id} → ${decision === 'approve' ? 'resolved' : 'rejected'}.` });
        this.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Resolution failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const tabBtn = (t) => ({ ...btn(this.state.tab === t ? RED : '#fff'), color: this.state.tab === t ? '#fff' : INK, border: this.state.tab === t ? 'none' : '1px solid #ddd' });
    const stBadge = (st) => {
      const c = { resolved: '#2E7D32', open: '#F9A825', rejected: '#9E9E9E' }[st] || '#9E9E9E';
      return { padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', background: c };
    };
    const typeBadge = (t) => {
      const c = { time: '#1565C0', materials: '#6A1B9A', workers: '#00838F' }[t] || '#555';
      return { padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: c };
    };

    const q = this.props.contestsData && this.props.contestsData.getWorkerRequestContests;
    const contests = (q && q.results) || [];
    const openCount = (q && q.openCount) || 0;
    const count = (q && q.count) || 0;
    const PAGE_SIZE = 10;
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Job Contests</h1>
        <p style={{ color: '#777', marginTop: -8, marginBottom: 16 }}>
          When a client contests a worker&apos;s mid-job request (extra time, materials or workers),
          it lands here. Approve applies it just like the client accepting — optionally at a fair price.
        </p>

        <div style={{ marginBottom: 16 }}>
          <button style={tabBtn('open')} onClick={() => this.switchTab('open')}>Open {openCount ? `(${openCount})` : ''}</button>
          <button style={tabBtn('resolved')} onClick={() => this.switchTab('resolved')}>Resolved</button>
          <button style={tabBtn('rejected')} onClick={() => this.switchTab('rejected')}>Rejected</button>
          {this.state.message ? <span style={{ marginLeft: 12, color: RED, fontWeight: 600 }}>{this.state.message}</span> : null}
        </div>

        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
              <th style={{ padding: 10 }}>#</th>
              <th style={{ padding: 10 }}>Booking</th>
              <th style={{ padding: 10 }}>Type</th>
              <th style={{ padding: 10 }}>Worker asked</th>
              <th style={{ padding: 10 }}>Client / Worker</th>
              <th style={{ padding: 10 }}>Reason / proposed</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }}>Actions</th>
            </tr></thead>
            <tbody>
              {contests.length === 0 && <tr><td colSpan={8} style={{ padding: 20 }}>No {this.state.tab} contests.</td></tr>}
              {contests.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid #f0f0f0', verticalAlign: 'top' }}>
                  <td style={{ padding: 10 }}>{c.id}</td>
                  <td style={{ padding: 10 }}>#{c.bookingId}</td>
                  <td style={{ padding: 10 }}><span style={typeBadge(c.requestType)}>{c.requestType}</span></td>
                  <td style={{ padding: 10 }}>
                    {c.requestDetail || '—'}
                    {c.requestAmount != null ? <div style={{ color: RED, fontWeight: 600 }}>{c.currency || ''} {c.requestAmount}</div> : null}
                    {c.requestStatus ? <div style={{ fontSize: 11, color: '#999' }}>row: {c.requestStatus}</div> : null}
                  </td>
                  <td style={{ padding: 10 }}>{c.clientName || c.userId || '—'}<div style={{ fontSize: 12, color: '#888' }}>{c.partnerName || ''}</div></td>
                  <td style={{ padding: 10 }}>
                    {c.reason ? <div style={{ whiteSpace: 'pre-wrap' }}>{c.reason}</div> : <span style={{ color: '#bbb' }}>—</span>}
                    {c.proposedAmount != null ? <div style={{ fontSize: 12, color: '#1565C0' }}>proposes: {c.currency || ''} {c.proposedAmount}</div> : null}
                  </td>
                  <td style={{ padding: 10 }}><span style={stBadge(c.status)}>{c.status}</span></td>
                  <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                    {c.status === 'open' ? (
                      <>
                        <button style={btn('#2E7D32')} disabled={this.state.busy} onClick={() => this.resolve(c, 'approve')}>Approve</button>
                        <button style={btn('#9E9E9E')} disabled={this.state.busy} onClick={() => this.resolve(c, 'reject')}>Reject</button>
                      </>
                    ) : (
                      <span style={{ color: '#aaa' }}>{c.resolvedAt ? c.resolvedAt.slice(0, 10) : 'closed'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {count > PAGE_SIZE && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={btn(RED)} disabled={this.state.currentPage <= 1} onClick={() => this.go(-1)}>Prev</button>
            <span>Page {this.state.currentPage} / {totalPages} ({count})</span>
            <button style={btn(RED)} disabled={this.state.currentPage >= totalPages} onClick={() => this.go(1)}>Next</button>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withQuery(getWorkerRequestContests, { name: 'contestsData', options: { variables: { currentPage: 1, searchList: 'open' }, fetchPolicy: 'network-only', ssr: false } }),
  withMutation(resolveWorkerRequestContestM, { name: 'resolveMut' }),
)(JobContests);
